import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useClients } from "../../hooks/useClients";
import Reveal from "../Reveal";

/**
 * Real client logos sourced from yastikmedya.com — render as a continuous
 * marquee strip in grayscale → colour-on-hover style. Logos can be managed
 * via Admin → Müşteriler. Falls back to bundled logos shipped under
 * /public/brand-clients/ when admin DB is empty.
 */
const FALLBACK_CLIENTS = [
  { id: "bahcesehir-uni", name: "Bahçeşehir Üniversitesi", logo: "/brand-clients/bahcesehir-uni.png", website: "https://bau.edu.tr/" },
  { id: "ugur-okullari", name: "Uğur Okulları", logo: "/brand-clients/ugur-okullari.png", website: "https://www.ugurokullari.k12.tr/" },
  { id: "praktiker", name: "Praktiker", logo: "/brand-clients/praktiker.png", website: "https://praktiker.com.tr/" },
  { id: "izocam", name: "İzocam", logo: "/brand-clients/izocam.png", website: "https://www.izocam.com.tr/" },
  { id: "amorium-hotel", name: "Amorium Hotel", logo: "/brand-clients/amorium-hotel.png", website: "https://amoriumhotel.com/" },
  { id: "muscle-station", name: "Muscle Station", logo: "/brand-clients/muscle-station.webp", website: "https://www.musclestation.com.tr/" },
  { id: "hb-dijital-logo", name: "HB Dijital", logo: "/brand-clients/hb-dijital-logo.png", website: "https://hbdijitalbaski.com.tr/" },
  { id: "bigistinvest", name: "Bigist Invest", logo: "/brand-clients/bigistinvest.png", website: "https://www.instagram.com/bigistinvest/" },
  { id: "potensas", name: "Potensas", logo: "/brand-clients/potensas.png", website: "https://www.potensas.com/" },
  { id: "uskudar-sinav", name: "Üsküdar Sınav Koleji", logo: "/brand-clients/uskudar-sinav.webp", website: "https://www.uskudarsinavkoleji.com/" },
  { id: "tssf", name: "TSSF", logo: "/brand-clients/tssf.png", website: "https://tssf.gov.tr/" },
  { id: "visamerika", name: "Visamerika", logo: "/brand-clients/visamerika.png", website: "https://www.visamerika.com/" },
];

function ClientLogoTile({ c }) {
  const slug = c.id || c.name?.toLowerCase().replace(/\s+/g, "-");
  return (
    <a
      href={c.website || "#"}
      target="_blank"
      rel="noopener noreferrer"
      title={c.name}
      data-testid={`client-logo-${slug}`}
      className="group/c shrink-0 inline-flex items-center justify-center h-16 sm:h-20 px-6 sm:px-8 mx-2"
    >
      <img
        src={c.logo}
        alt={c.name}
        loading="lazy"
        className="max-h-10 sm:max-h-12 w-auto object-contain
                   grayscale opacity-60 transition-[filter,opacity,transform] duration-300 ease-out
                   group-hover/c:grayscale-0 group-hover/c:opacity-100 group-hover/c:scale-105"
      />
    </a>
  );
}

export default function ClientsMarquee() {
  const { t } = useLanguage();
  const dynamicClients = useClients();
  // Use admin data if any rows exist, otherwise show bundled fallback set
  const source = (dynamicClients && dynamicClients.length > 0) ? dynamicClients : FALLBACK_CLIENTS;
  // Filter out entries lacking a logo URL, then duplicate for seamless loop
  const cleaned = source.filter((c) => c.logo);
  const list = cleaned.length > 0 ? [...cleaned, ...cleaned] : [];

  return (
    <section
      data-testid="clients-marquee"
      className="relative bg-cream py-section border-t border-mist"
    >
      <div className="container-page">
        <Reveal as="div" className="max-w-2xl mx-auto text-center">
          <span className="eyebrow inline-flex">
            <span className="h-1 w-1 rounded-full bg-brand-pink" />
            {t("clients.eyebrow")}
          </span>
          <h2 className="mt-4 font-display text-h1 font-semibold text-ink">
            {t("clients.title_pre")}{" "}
            <span className="gold-foil-text">{t("clients.title_accent")}</span>
          </h2>
        </Reveal>
      </div>

      {/* Edge fade gradients */}
      <div className="relative mt-12">
        <div className="pointer-events-none absolute inset-y-0 start-0 w-24 sm:w-40 bg-gradient-to-r from-cream to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 end-0 w-24 sm:w-40 bg-gradient-to-l from-cream to-transparent z-10" />

        <div className="overflow-hidden">
          <div className="flex w-max items-center animate-marquee-x [animation-duration:46s] hover:[animation-play-state:paused]">
            {list.map((c, i) => (
              <ClientLogoTile key={`${c.id || c.name}-${i}`} c={c} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
